/**
 * PODPISY DO MAILOV.
 *
 * Zamestnanec ich má viac – iný pod cenovú ponuku, iný pod servis, kratší na
 * rýchlu odpoveď –, ale **jeden je primárny**: ten sa doplní sám pod každý
 * mail. Ostatné sa dajú vybrať pri písaní konkrétnej odpovede v Schránke.
 *
 * Vľavo zoznam, vpravo úprava a náhľad presne v tom tvare, ako to uvidí
 * zákazník. Náhľad chodí zo servera – ukazuje aj doplnené premenné
 * ({{meno}}) aj to, čo z podpisu odstránilo čistenie.
 */
import { useEffect, useState } from 'react';
import { Star, Trash2, Plus, Save } from 'lucide-react';
import { api } from '../api';
import { Input, Textarea, Select, useToast } from './ui';

/** Vzor podpisu – premenné sa doplnia až pri odosielaní mailu. */
export const VZOR_PODPISU = `<div style="font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#333">
  <div style="font-weight:bold;font-size:14px;color:#111">{{meno}}</div>
  <div style="color:#666">{{firma}}</div>
  <div style="margin-top:6px">
    tel.: <a href="tel:{{telefon}}" style="color:#666;text-decoration:none">{{telefon}}</a><br>
    <a href="mailto:{{email}}" style="color:#666">{{email}}</a><br>
    <a href="{{web}}" style="color:#b91c1c">{{web}}</a>
  </div>
  <div style="margin-top:6px;color:#999;font-size:12px">{{adresa}}</div>
</div>`;

const PREMENNE = [
  ['{{meno}}', 'meno zamestnanca'],
  ['{{email}}', 'jeho e-mail'],
  ['{{firma}}', 'názov firmy'],
  ['{{telefon}}', 'telefón firmy'],
  ['{{web}}', 'web firmy'],
  ['{{adresa}}', 'adresa firmy'],
];

const prazdny = () => ({ id: null, name: '', html: VZOR_PODPISU, box_id: null });

/**
 * @param {number|null} userId  koho podpisy – prázdne = prihlásený
 * @param {function} onZmena    zavolá sa po uložení (obnovenie zoznamov vonku)
 */
export default function PodpisEditor({ userId = null, onZmena }) {
  const toast = useToast();
  const [kto, setKto] = useState(userId);
  const [zoznam, setZoznam] = useState(null);
  const [vybrany, setVybrany] = useState(null);   // rozpracovaný podpis
  const [nahlad, setNahlad] = useState('');
  const [busy, setBusy] = useState(false);
  /* Schránky značiek. Podpis pridelený schránke sa v nej doplní sám a inde
     sa neponúkne – kto píše pod dvoma menami, nemá jeden „ten svoj" podpis. */
  const [schranky, setSchranky] = useState([]);

  const nacitaj = async (oznacId = null) => {
    try {
      const v = userId
        ? await api.get(`/api/auth/users/${userId}/podpisy`)
        : await api.get('/api/auth/podpisy');
      setKto(v.id);
      setZoznam(v.podpisy);
      const chce = oznacId
        ? v.podpisy.find((p) => p.id === oznacId)
        : v.podpisy.find((p) => p.is_primary) || v.podpisy[0];
      setVybrany(chce ? { ...chce } : null);
      setNahlad(chce ? '' : v.nahlad);
      return v.id;
    } catch (e) { toast.error(e.message); return null; }
  };

  useEffect(() => { nacitaj(); }, [userId]);
  useEffect(() => {
    api.get('/api/schranka/schranky')
      .then((v) => setSchranky(v.schranky || []))
      .catch(() => setSchranky([]));   // kto do Schránky nevidí, nemá čo prideľovať
  }, []);

  /* Náhľad sa pýta servera, aby ukázal presne to, čo sa odošle. */
  useEffect(() => {
    if (!kto || !vybrany?.id) return;
    api.get(`/api/auth/users/${kto}/podpis/nahlad?podpis_id=${vybrany.id}`)
      .then((v) => setNahlad(v.html || ''))
      .catch(() => {});
  }, [kto, vybrany?.id]);

  const uloz = async () => {
    if (!kto || !vybrany) return;
    setBusy(true);
    try {
      const v = await api.post(`/api/auth/users/${kto}/podpisy`, {
        podpis_id: vybrany.id, name: vybrany.name, html: vybrany.html,
        box_id: vybrany.box_id || null,
      });
      toast.success('Podpis uložený.');
      await nacitaj(v.id);
      onZmena?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const primarny = async (p) => {
    setBusy(true);
    try {
      await api.post(`/api/auth/users/${kto}/podpisy/${p.id}/primarny`, {});
      toast.success(`„${p.name}“ sa bude používať automaticky.`);
      await nacitaj(p.id);
      onZmena?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const zmaz = async (p) => {
    if (!window.confirm(`Zmazať podpis „${p.name}“?`)) return;
    setBusy(true);
    try {
      await api.del(`/api/auth/users/${kto}/podpisy/${p.id}`);
      await nacitaj();
      onZmena?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const doc = `<!doctype html><html><head><meta charset="utf-8"><style>
    html,body{margin:0;padding:8px;background:#fff;font-family:Arial,Helvetica,sans-serif;font-size:13px}
    img{max-width:100%}
  </style></head><body>${nahlad}</body></html>`;

  if (!zoznam) return <p className="text-sm text-slate-500">Načítavam…</p>;

  return (
    <div className="grid gap-3 lg:grid-cols-[220px_minmax(0,1fr)]">
      {/* zoznam podpisov */}
      <div className="space-y-1.5">
        {zoznam.map((p) => (
          <div key={p.id}
            className={`rounded-lg border p-2 text-sm ${
              vybrany?.id === p.id ? 'border-brand-300 bg-brand-50' : 'border-slate-200 bg-white'}`}>
            <button className="block w-full truncate text-left font-medium"
              onClick={() => setVybrany({ ...p })}>
              {p.name}
            </button>
            {!!p.box_id && (
              <div className="truncate text-[11px] text-slate-500">
                {schranky.find((b) => b.id === p.box_id)?.nazov || 'iná schránka'}
              </div>
            )}
            <div className="mt-1 flex items-center gap-1">
              {p.is_primary ? (
                <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800">
                  <Star size={11} /> používa sa
                </span>
              ) : (
                <button className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-slate-500 hover:bg-slate-100"
                  disabled={busy} onClick={() => primarny(p)}>
                  <Star size={11} /> používať tento
                </button>
              )}
              <button className="ml-auto rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                title="Zmazať" disabled={busy} onClick={() => zmaz(p)}>
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
        <button className="btn-secondary w-full !py-1.5 text-xs"
          onClick={() => { setVybrany(prazdny()); setNahlad(''); }}>
          <Plus size={14} /> Nový podpis
        </button>
        {!zoznam.length && (
          <p className="pt-1 text-xs text-slate-500">
            Zatiaľ žiadny vlastný podpis – používa sa predvolený podpis firmy.
          </p>
        )}
      </div>

      {/* úprava a náhľad */}
      {!vybrany ? (
        <p className="text-sm text-slate-500">Vyberte podpis vľavo alebo pridajte nový.</p>
      ) : (
        <div className="grid gap-3 xl:grid-cols-2">
          <div>
            <Input label="Názov podpisu" value={vybrany.name} placeholder="napr. Servis"
              onChange={(e) => setVybrany((v) => ({ ...v, name: e.target.value }))}
              hint="Len pre vás – zákazník ho nevidí." />
            {schranky.length > 1 && (
              <div className="mt-2">
                <Select label="Používať v schránke" value={String(vybrany.box_id || '')}
                  onChange={(e) => setVybrany((v) => ({ ...v, box_id: Number(e.target.value) || null }))}
                  options={[{ value: '', label: 'vo všetkých' },
                    ...schranky.map((b) => ({ value: String(b.id), label: b.nazov }))]}
                  hint="Podpis pridelený schránke sa v nej doplní sám – a inde sa neponúkne." />
              </div>
            )}
            <div className="mt-2">
              <Textarea label="Podpis (HTML)" rows={12} value={vybrany.html} spellCheck={false}
                className="font-mono"
                onChange={(e) => setVybrany((v) => ({ ...v, html: e.target.value }))} />
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button className="btn-primary !py-1.5 text-xs" disabled={busy} onClick={uloz}>
                <Save size={14} /> Uložiť podpis
              </button>
              <button className="btn-secondary !py-1.5 text-xs"
                onClick={() => setVybrany((v) => ({ ...v, html: VZOR_PODPISU }))}>
                Vložiť vzor
              </button>
            </div>
            <div className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
              <div className="mb-1 font-medium text-slate-700">Doplní sa pri odoslaní:</div>
              {PREMENNE.map(([k, popis]) => (
                <div key={k}><code className="text-slate-800">{k}</code> — {popis}</div>
              ))}
            </div>
          </div>
          <div>
            <span className="label">Ako to uvidí zákazník</span>
            <iframe title="Náhľad podpisu" srcDoc={doc} sandbox="allow-same-origin"
              className="h-64 w-full rounded-lg border border-slate-200 bg-white" />
            <p className="mt-2 text-xs text-slate-500">
              Podpis označený <b>používa sa</b> sa pripojí pod každý mail odoslaný zo systému.
              Iný podpis sa dá vybrať pri písaní odpovede v Schránke. Náhľad sa obnoví
              po uložení.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
